import { Link } from 'react-router-dom';
import { Star, MapPin, ShieldCheck, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };
const COIN_SYMBOLS = { BTC:'₿', ETH:'Ξ', BNB:'BNB', SOL:'◎', USDT:'₮', SUI:'SUI', ASTER:'AST', HYPE:'HYPE', XRP:'✕' };

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { _id, title, images, displayPrice, displayPriceWithFee, displayCurrency, priceUSD, rating, reviewCount, shipsFrom, seller } = product;
  const color = COIN_COLORS[displayCurrency] || '#FF6B00';
  const symbol = COIN_SYMBOLS[displayCurrency] || displayCurrency;
  const img = images?.[0]?.url || 'https://via.placeholder.com/300x300/12121A/FF6B00?text=CM';

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast.success('Added to cart!');
  };

  return (
    <Link to={`/product/${_id}`} className="product-card block rounded-2xl overflow-hidden"
      style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-brand-card">
        <img src={img} alt={title} className="w-full h-full object-cover" loading="lazy"
          onError={e => { e.target.src = 'https://via.placeholder.com/300x300/12121A/FF6B00?text=CM'; }} />
        {/* Currency badge */}
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold"
          style={{ background: `${color}20`, color, border: `1px solid ${color}40`, fontFamily: 'JetBrains Mono' }}>
          {symbol}
        </div>
        {/* Quick add to cart */}
        <button onClick={handleAddToCart}
          className="absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
          <Plus size={16} className="text-white" />
        </button>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-brand-text text-sm font-medium leading-tight line-clamp-2 mb-2" style={{ fontFamily: 'DM Sans' }}>{title}</p>

        {/* Price */}
        <div className="mb-2">
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold" style={{ color, fontFamily: 'JetBrains Mono' }}>
              {displayPriceWithFee?.toFixed(displayCurrency === 'BTC' ? 6 : 4) || '—'}
            </span>
            <span className="text-xs font-semibold" style={{ color }}>{displayCurrency}</span>
          </div>
          <p className="text-xs text-brand-muted">≈ ${(priceUSD * 1.015).toFixed(2)} USD <span className="opacity-50">(incl. 1.5% fee)</span></p>
        </div>

        {/* Rating + location */}
        <div className="flex items-center justify-between text-xs text-brand-muted">
          <div className="flex items-center gap-1">
            <Star size={11} fill="#F5A623" stroke="none" />
            <span>{rating?.toFixed(1) || '0.0'}</span>
            <span className="opacity-50">({reviewCount || 0})</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin size={11} />
            <span>{shipsFrom?.country || 'Global'}</span>
          </div>
        </div>

        {/* Verified badge */}
        {seller?.store?.rating >= 4.5 && (
          <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: '#00C864' }}>
            <ShieldCheck size={11} /> <span>Verified Seller</span>
          </div>
        )}
      </div>
    </Link>
  );
}
